/**
 * OP Readiness — Stage 7b runtime CONTROL panel (pause/resume the dispatch loops via the kill-switch).
 *
 * Reads/writes /api/op-readiness/dispatch-control (sync.DispatchControl, mig 054). Pausing halts the
 * scheduler's enqueue and forces the consumer to DRY_RUN at RUNTIME (no redeploy). It NEVER enables
 * live writes — going LIVE still needs the config liveWriteEnabled flag + the env token (the worker
 * triple-lock). So "Resume" only returns the loop to its config-gated behaviour (inert by default).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchDispatchControl, setDispatchControl, type DispatchControlRow } from '@/lib/op-readiness';
import { formatDateTime } from './shared';

const TARGET_LABEL: Record<string, string> = { op: 'Open Publishing', hubspot: 'HubSpot' };

export function DispatchControlPanel() {
  const qc = useQueryClient();
  const controlQuery = useQuery({
    queryKey: ['op-readiness', 'dispatch-control'],
    queryFn: fetchDispatchControl,
    refetchInterval: 15000,
  });
  const mutation = useMutation({
    mutationFn: ({ target, kill }: { target: 'op' | 'hubspot'; kill: boolean }) =>
      setDispatchControl(target, kill, kill ? 'paused via Stage-7b UI' : 'resumed via Stage-7b UI'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['op-readiness', 'dispatch-control'] }),
  });

  const controls = controlQuery.data?.controls ?? [];

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Runtime control — kill-switch</h3>
        {mutation.isError && (
          <span className="text-xs text-destructive">Failed — is migration 054 applied on this DB?</span>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Pause halts enqueue and forces the consumer to DRY_RUN at runtime (no redeploy). It never enables
        live writes — those still require the config flag + env token (the worker triple-lock).
      </p>
      {controlQuery.isLoading && <Skeleton className="h-20 w-full rounded-lg" />}
      {!controlQuery.isLoading && controls.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No control rows — apply migration 054_dispatch_control on this database.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {controls.map((c) => (
          <ControlCard
            key={c.target}
            c={c}
            busy={mutation.isPending}
            onToggle={() => mutation.mutate({ target: c.target, kill: !c.killSwitch })}
          />
        ))}
      </div>
    </div>
  );
}

function ControlCard({
  c,
  busy,
  onToggle,
}: {
  c: DispatchControlRow;
  busy: boolean;
  onToggle: () => void;
}) {
  const paused = c.killSwitch;
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{TARGET_LABEL[c.target] ?? c.target}</div>
          <div className={`text-xs ${paused ? 'text-destructive' : 'text-emerald-600'}`}>
            {paused ? '⏸ PAUSED (kill-switch ON)' : '▶ Running (kill-switch off)'}
          </div>
        </div>
        <Button
          size="sm"
          variant={paused ? 'default' : 'destructive'}
          disabled={busy}
          onClick={onToggle}
        >
          {paused ? 'Resume' : 'Pause'}
        </Button>
      </div>
      {(c.updatedBy || c.note) && (
        <div className="mt-2 text-xs text-muted-foreground">
          {c.updatedBy && <span>by {c.updatedBy}</span>}
          {c.updatedAt && <span> · {formatDateTime(c.updatedAt)}</span>}
          {c.note && <span> · {c.note}</span>}
        </div>
      )}
    </div>
  );
}
